import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, X, ImageSquare } from "@phosphor-icons/react";
import { toast } from "sonner";

const SLOTS = [
  "Frontal",
  "Halbprofil links",
  "Halbprofil rechts",
  "Profil links",
  "Profil rechts",
  "Hinterkopf",
  "Ganzkörper",
];

export default function NewAnalysis() {
  const navigate = useNavigate();
  const [gender, setGender] = useState("damen");
  const [name, setName] = useState("");
  const [beruf, setBeruf] = useState("");
  const [pflege, setPflege] = useState("");
  const [stilwunsch, setStilwunsch] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const onDrop = useCallback((accepted) => {
    setFiles((prev) => [...prev, ...accepted].slice(0, 7));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/jpeg": [], "image/png": [], "image/webp": [] },
    maxFiles: 7,
  });

  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);

  const removeAt = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (files.length === 0) {
      toast.error("Bitte mindestens ein Foto hinzufügen");
      return;
    }
    setBusy(true);
    const form = new FormData();
    form.append("gender", gender);
    form.append("customer_name", name);
    form.append("questionnaire", JSON.stringify({ beruf, pflegeaufwand: pflege, stilwunsch }));
    files.forEach((f) => form.append("photos", f));
    try {
      const { data } = await api.post("/analyses", form);
      navigate(`/analyse/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-8 lg:px-12 py-16 lg:py-24" data-testid="new-analysis-page">
      <div className="mb-16 rise">
        <p className="label-xs mb-5">Schritt für Schritt</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">Neue Analyse</h1>
      </div>

      <div className="grid lg:grid-cols-[420px_1fr] gap-16 lg:gap-24">
        <div className="space-y-12 rise">
          <div>
            <p className="label-xs mb-5">Beratungsart</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "damen", label: "Damen" },
                { key: "herren", label: "Herren" },
              ].map((g) => (
                <button
                  key={g.key}
                  data-testid={`gender-${g.key}-button`}
                  onClick={() => setGender(g.key)}
                  className={`h-14 rounded-sm border text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-200 ${
                    gender === g.key
                      ? "bg-foreground text-primary-foreground border-foreground"
                      : "bg-card border-border text-muted-foreground hover:border-foreground/40"
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="label-xs mb-5">Kundenname</p>
            <Input
              data-testid="customer-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vor- und Nachname"
              className="h-12 rounded-sm bg-white focus-visible:ring-1 focus-visible:ring-foreground"
            />
          </div>

          <div className="space-y-8">
            <p className="label-xs">Kurz-Fragebogen — optional</p>
            <div>
              <label className="text-xs text-muted-foreground block mb-3">Beruf / Alltag</label>
              <Input
                data-testid="questionnaire-beruf-input"
                value={beruf}
                onChange={(e) => setBeruf(e.target.value)}
                placeholder="z. B. Rechtsanwältin, viel Kundenkontakt"
                className="h-12 rounded-sm bg-white focus-visible:ring-1 focus-visible:ring-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-3">Pflegeaufwand</label>
              <Select value={pflege} onValueChange={setPflege}>
                <SelectTrigger
                  data-testid="questionnaire-pflege-select"
                  className="h-12 rounded-sm bg-white focus:ring-1 focus:ring-foreground"
                >
                  <SelectValue placeholder="Bitte wählen" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="minimal">Minimal — pflegeleicht</SelectItem>
                  <SelectItem value="moderat">Moderat — täglich kurz stylen</SelectItem>
                  <SelectItem value="hoch">Hoch — aufwendiges Styling willkommen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-3">Stilwunsch</label>
              <Textarea
                data-testid="questionnaire-stilwunsch-input"
                value={stilwunsch}
                onChange={(e) => setStilwunsch(e.target.value)}
                placeholder="z. B. klassisch-elegant, gerne etwas Veränderung bei der Länge"
                className="min-h-[110px] rounded-sm bg-white focus-visible:ring-1 focus-visible:ring-foreground"
              />
            </div>
          </div>
        </div>

        <div className="rise">
          <div className="flex items-baseline justify-between mb-6">
            <p className="label-xs">Aufnahmen — 6 Portrait, 1 Ganzkörper</p>
            <span data-testid="photo-count" className="text-xs text-muted-foreground">
              {files.length} / 7
            </span>
          </div>

          <div
            {...getRootProps()}
            data-testid="photo-dropzone"
            className={`border border-dashed rounded-sm p-12 text-center cursor-pointer transition-colors duration-200 ${
              isDragActive ? "border-foreground bg-accent/40" : "border-input bg-card hover:border-foreground/40"
            }`}
          >
            <input {...getInputProps()} data-testid="photo-input" />
            <Camera size={28} className="mx-auto mb-5 text-muted-foreground" />
            <p className="text-sm font-medium">Fotos hierher ziehen oder auswählen</p>
            <p className="mt-2 text-xs text-muted-foreground">
              JPG, PNG oder WEBP — Kamera am Tablet wird unterstützt
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {SLOTS.map((slot, idx) => (
              <div key={slot} data-testid={`photo-slot-${idx}`}>
                <div
                  className={`aspect-[3/4] rounded-sm border overflow-hidden relative flex items-center justify-center ${
                    previews[idx] ? "border-border" : "border-dashed border-input bg-secondary/40"
                  }`}
                >
                  {previews[idx] ? (
                    <>
                      <img src={previews[idx]} alt={slot} className="h-full w-full object-cover" />
                      <button
                        data-testid={`photo-remove-${idx}`}
                        onClick={() => removeAt(idx)}
                        className="absolute top-2 right-2 h-7 w-7 rounded-sm bg-white/90 flex items-center justify-center hover:bg-white transition-colors duration-200"
                      >
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <ImageSquare size={22} className="text-muted-foreground/50" />
                  )}
                </div>
                <p className="mt-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{slot}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-6">
            <Button
              data-testid="submit-analysis-button"
              onClick={submit}
              disabled={busy}
              className="h-14 px-12 rounded-sm bg-foreground text-primary-foreground text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-foreground/90 hover:-translate-y-[1px] transition-transform duration-200"
            >
              {busy ? "Wird gestartet …" : "Analyse starten"}
            </Button>
            <p className="text-xs text-muted-foreground max-w-xs leading-relaxed">
              Die Auswertung dauert rund zwei bis vier Minuten. Du kannst das Fenster geöffnet lassen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
